// Queries y mutations de la bandeja de correcciones de paro del Supervisor.
//
// Feature-local (mismo criterio que features/mantenimiento/queries.ts) para no
// seguir engordando lib/api/queries.ts. Mismo cliente (apiGet/apiPatch), mismos
// contratos Zod compartidos (packages/schemas) y misma convención de keys del
// rol: todas cuelgan de ['supervisor', 'correcciones-paro'], y las mutations
// invalidan la raíz ['supervisor'] entera (igual que resolver/descartar
// incidencias en lib/api/queries.ts) porque aprobar una corrección recalcula
// minutos_perdidos y eso mueve KPIs del dashboard.

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  correccionesParoBandejaResponseSchema,
  correccionParoAprobarSchema,
  correccionParoRechazarSchema,
  correccionParoResueltaResponseSchema,
  type CorreccionParoAprobar,
  type CorreccionParoRechazar,
  type EstadoValidacionParo,
} from 'schemas';
import { apiGet, apiPatch } from '../../lib/api/client';

export const correccionesParoKeys = {
  root: ['supervisor', 'correcciones-paro'] as const,
  lista: (estado: EstadoValidacionParo, plantaId?: string) =>
    ['supervisor', 'correcciones-paro', estado, plantaId ?? ''] as const,
};

/**
 * Bandeja de correcciones propuestas por el técnico de mantenimiento.
 * Poll 30s: es una bandeja operativa (mismo criterio que paros-pendientes).
 */
export function useCorreccionesParo(estado: EstadoValidacionParo, plantaId?: string) {
  return useQuery({
    queryKey: correccionesParoKeys.lista(estado, plantaId),
    queryFn: () => {
      const params = new URLSearchParams({ estado });
      if (plantaId) params.set('planta_id', plantaId);
      return apiGet(
        `/api/supervisor/correcciones-paro?${params.toString()}`,
        correccionesParoBandejaResponseSchema,
      );
    },
    staleTime: 15_000,
    refetchInterval: 30_000,
    placeholderData: keepPreviousData,
  });
}

/** Invalida la bandeja + el resto del rol (dashboard/KPIs derivan de los paros). */
function useInvalidarSupervisor() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: correccionesParoKeys.root });
    void qc.invalidateQueries({ queryKey: ['supervisor'] });
  };
}

export function useAprobarCorreccionParo() {
  const invalidar = useInvalidarSupervisor();
  return useMutation({
    mutationFn: (args: { id: string; input: CorreccionParoAprobar }) =>
      apiPatch(
        `/api/supervisor/correcciones-paro/${args.id}/aprobar`,
        correccionParoAprobarSchema.parse(args.input),
        correccionParoResueltaResponseSchema,
      ),
    onSuccess: invalidar,
  });
}

export function useRechazarCorreccionParo() {
  const invalidar = useInvalidarSupervisor();
  return useMutation({
    mutationFn: (args: { id: string; input: CorreccionParoRechazar }) =>
      apiPatch(
        `/api/supervisor/correcciones-paro/${args.id}/rechazar`,
        correccionParoRechazarSchema.parse(args.input),
        correccionParoResueltaResponseSchema,
      ),
    onSuccess: invalidar,
  });
}
